import React, { useEffect, useState, useCallback } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle } from "lucide-react";
import EmptyState from "@/components/shared/EmptyState";

const formatEur = (v) => `${(v || 0).toFixed(2)} €`;

export default function WasteCriticalProducts({ venueId, settings, refreshKey }) {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const data = await wasteApi.getCriticalProducts(venueId, 30);
      setItems(data.items || []);
      setThreshold(data.threshold || 3);
    } catch (e) { /* noop */ } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const reasons = settings?.waste_reasons || [];
  const reasonLabel = (c) => reasons.find((r) => r.code === c)?.label || c;

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#7B2D3F]" /></div>;

  return (
    <div className="space-y-4" data-testid="waste-critical">
      <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-900">Productes crítics</p>
          <p className="text-sm text-amber-800">
            Es mostren productes amb <strong>{threshold}+</strong> registres de merma en els últims 30 dies.
            Considera revisar els processos de compra, conservació o porcionatge.
          </p>
        </div>
      </Card>

      {items.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="Cap producte crític"
          description="Encara no hi ha productes amb merma recurrent. Bona feina!" />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((it, i) => (
            <Card key={`${it.product_name}-${i}`} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{it.product_name}</p>
                  <p className="text-xs text-gray-500">Últim registre: {it.last_seen}</p>
                </div>
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{it.count} registres</Badge>
              </div>
              <div className="mt-3 flex items-center gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Cost total</p>
                  <p className="font-bold text-[#7B2D3F]">{formatEur(it.total_cost)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Quantitat</p>
                  <p className="font-bold text-gray-900">{(it.total_quantity || 0).toFixed(2)}</p>
                </div>
              </div>
              {it.reasons?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {it.reasons.map((r) => (
                    <Badge key={r} variant="secondary" className="text-xs">{reasonLabel(r)}</Badge>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
